import React, { useState } from 'react';
import { Card, Row, Col, Button, Space, Typography, message } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import './DiagnosticForm.css';
import { SpecBadge } from './SpecBadge';
import { VerdictBar } from './VerdictBar';
import { diagnosticService } from '../../services/diagnosticService';
import type { Run, Procedure } from '../../services/diagnosticService';

const { Text } = Typography;

export interface DiagnosticDetailProps {
  run: Run;
  procedure: Procedure;
}

export const DiagnosticDetail: React.FC<DiagnosticDetailProps> = ({ run, procedure }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { data } = await diagnosticService.downloadPdf(run.id);
      // Create a temporary object URL and trigger download. Browser receives a Blob.
      const url = window.URL.createObjectURL(data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `diagnostic-${run.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(e?.response?.data?.error ?? e?.message ?? 'pdf download failed');
    } finally {
      setDownloading(false);
    }
  };

  const units = procedure.inputs_schema?.cell?.units ?? procedure.display_config?.units ?? '';

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Card size="small">
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text strong>{procedure.name}</Text>
          <SpecBadge
            source={run.spec_used?.source}
            attribution={run.spec_used?.attribution_text}
            min={run.spec_used?.min}
            max={run.spec_used?.max}
            units={units}
          />
        </Space>
      </Card>

      <VerdictBar verdict={run.overall_verdict} />

      <Row gutter={[8, 8]}>
        {(run.results ?? []).map((c) => (
          <Col xs={12} sm={6} key={c.cell}>
            <Card size="small" className={`diag-cell diag-cell--${c.verdict}`}>
              <div className="diag-cell-label">{c.cell.toUpperCase()}</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{c.value}</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Button icon={<FilePdfOutlined />} onClick={handleDownload} loading={downloading}>
        Download PDF
      </Button>
    </Space>
  );
};
